import { useEffect, useMemo, useRef, useState } from 'react';
import useQuery from './useQuery';
import { useHistory } from 'react-router-dom';
import { selectBrandList } from 'redux/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrentBusiness } from 'redux/actions/business';
import instance from 'services/api';

const useUpdateBusiness = () => {
  const dispatch = useDispatch();
  const query = useQuery();
  const history = useHistory();
  const { currentBusiness } = useSelector((state) => state.business);
  const brandListData = useSelector(selectBrandList);
  const [brandList, setBrandList] = useState(brandListData || []);
  // Refs to prevent infinite loops
  const lastProcessedSearchRef = useRef(null);
  const isProcessingRef = useRef(false);

  // Memoize query parameters
  const restaurantMailbox = useMemo(
    () => query.get('restaurantmailbox'),
    [query]
  );
  const businessUuid = useMemo(() => query.get('businessUuid'), [query]);

  useEffect(() => {
    // Memoize localStorage read to avoid multiple reads
    const showActiveBusiness = localStorage.getItem('showActiveBusiness');
    const isActive =
      showActiveBusiness === null ? true : Boolean(Number(showActiveBusiness));
    const verifiedQuery = isActive ? 'yes' : 'no';

    // Build search parameter once
    const searchParam = restaurantMailbox || businessUuid || '';

    // Create AbortController for request cancellation
    const abortController = new AbortController();

    // Make API request
    instance
      .get(
        `/users/me-v3?search=${encodeURIComponent(
          searchParam
        )}&limit=10&page=1&verified=${verifiedQuery}`,
        { signal: abortController.signal }
      )
      .then(({ data: { data } }) => {
        // Only update state if request wasn't cancelled
        if (!abortController.signal.aborted) {
          setBrandList(data?.brands || []);
        }
      })
      .catch((error) => {
        // Ignore abort errors, log others
        if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
          console.error('Failed to fetch brand list:', error);
        }
      });

    // Cleanup: cancel request if component unmounts or dependencies change
    return () => {
      abortController.abort();
    };
  }, [restaurantMailbox, businessUuid]);

  // Helper function to clean up query parameters
  const cleanupQueryParams = (searchParams) => {
    searchParams.delete('pid');
    searchParams.delete('cid');
    searchParams.delete('mode');
    return searchParams;
  };

  // Helper function to create location model
  const createLocationModel = (location) => ({
    value: location,
    label: location?.locationName,
    name: `${location?.locationName}`,
    disabled: location?.disabled,
    verified: location?.verified,
  });

  // Helper function to create brand model
  const createBrandModel = (brand) => ({
    id: brand._id,
    value: brand,
    label: brand?.brandName,
    name: `${brand?.brandName}`,
  });

  // Helper function to switch business
  const switchBusiness = (location, brand, identifier, identifierValue) => {
    const modelSelectedLocation = createLocationModel(location);
    const modelSelectedBrand = createBrandModel(brand);

    localStorage.setItem(
      'selectedLocation',
      JSON.stringify(modelSelectedLocation)
    );
    localStorage.setItem('selectedBrand', JSON.stringify(modelSelectedBrand));
    localStorage.setItem('alreadyLoaded', 'true');
    localStorage.removeItem('currentTab');

    // Store identifier (restaurantMailbox or businessUuid)
    if (identifier === 'restaurantMailbox') {
      localStorage.setItem(
        'restaurantmailbox',
        JSON.stringify(identifierValue)
      );
    } else if (identifier === 'businessUuid') {
      localStorage.setItem('businessUuid', identifierValue);
    }

    dispatch(getCurrentBusiness(location?.businessId));
    localStorage.setItem(
      'businessInfo',
      JSON.stringify({
        label: '',
        id: location?.businessId,
      })
    );

    window.location.reload();
  };

  // Helper function to find location by restaurantMailbox
  const findLocationByRestaurantMailbox = (mailbox) => {
    if (!brandList || !mailbox) return null;

    for (const brand of brandList) {
      if (!brand?.locations) continue;
      for (const location of brand.locations) {
        if (location.restaurantMailbox === mailbox) {
          return { location, brand };
        }
      }
    }
    return null;
  };

  // Helper function to find location by businessUuid
  const findLocationByBusinessUuid = (uuid) => {
    if (!brandList || !uuid) return null;

    for (const brand of brandList) {
      if (!brand?.locations) continue;
      for (const location of brand.locations) {
        if (location.businessUuid === uuid || location.uuid === uuid) {
          return { location, brand };
        }
      }
    }
    return null;
  };

  // Helper function to check if identifier changed
  const checkIdentifierChanged = (identifier, oldValue) => {
    if (!brandList || !oldValue) return false;

    for (const brand of brandList) {
      if (!brand?.locations) continue;
      for (const location of brand.locations) {
        if (identifier === 'restaurantMailbox') {
          if (location.restaurantMailbox !== oldValue) {
            return true;
          }
        } else if (identifier === 'businessUuid') {
          if (
            (location.businessUuid !== oldValue ||
              location.uuid !== oldValue) &&
            oldValue
          ) {
            return true;
          }
        }
      }
    }
    return false;
  };

  useEffect(() => {
    // Early return if brandList is not loaded
    if (!brandList || brandList.length === 0) {
      return;
    }

    // Prevent infinite loops - check if we've already processed this search
    const currentSearch = history.location.search || '';
    const normalizedSearch = currentSearch.replace(/^[?&]/, '?');

    if (
      isProcessingRef.current ||
      lastProcessedSearchRef.current === normalizedSearch
    ) {
      return;
    }

    // Read fresh values from localStorage inside effect
    const alreadyLoaded = localStorage.getItem('alreadyLoaded');
    const oldRestaurantMailbox = localStorage.getItem('restaurantmailbox');
    const oldBusinessUuid = localStorage.getItem('businessUuid');

    // Clean up query parameters for restaurantMailbox
    if (restaurantMailbox) {
      const searchParams = new URLSearchParams(history.location.search);
      const hasParamsToRemove =
        searchParams.has('pid') ||
        searchParams.has('cid') ||
        searchParams.has('mode');

      if (hasParamsToRemove) {
        cleanupQueryParams(searchParams);
        const newSearch = `?${searchParams.toString()}`;
        lastProcessedSearchRef.current = newSearch;
        history.replace({
          pathname: history.location.pathname,
          search: searchParams.toString(),
        });
        return; // Exit early to avoid processing during cleanup
      }
    }

    // Clean up query parameters for businessUuid
    if (businessUuid) {
      const searchParams = new URLSearchParams(history.location.search);
      const hasParamsToRemove =
        searchParams.has('pid') ||
        searchParams.has('cid') ||
        searchParams.has('mode');

      if (hasParamsToRemove) {
        cleanupQueryParams(searchParams);
        const newSearch = `?${searchParams.toString()}`;
        lastProcessedSearchRef.current = newSearch;
        history.replace({
          pathname: history.location.pathname,
          search: searchParams.toString(),
        });
        return; // Exit early to avoid processing during cleanup
      }
    }

    // Check if restaurantMailbox changed and remove alreadyLoaded
    if (checkIdentifierChanged('restaurantMailbox', oldRestaurantMailbox)) {
      localStorage.removeItem('alreadyLoaded');
    }

    // Check if businessUuid changed and remove alreadyLoaded
    if (checkIdentifierChanged('businessUuid', oldBusinessUuid)) {
      localStorage.removeItem('alreadyLoaded');
    }

    // Handle restaurantMailbox switching
    if (restaurantMailbox && !alreadyLoaded) {
      const found = findLocationByRestaurantMailbox(restaurantMailbox);
      if (found) {
        // Mark as processing and update last processed search
        isProcessingRef.current = true;
        lastProcessedSearchRef.current = normalizedSearch;

        switchBusiness(
          found.location,
          found.brand,
          'restaurantMailbox',
          restaurantMailbox
        );
        return; // Exit early after switching
      }
    }

    // Handle businessUuid switching
    if (businessUuid && !alreadyLoaded) {
      const found = findLocationByBusinessUuid(businessUuid);
      if (found) {
        // Mark as processing and update last processed search
        isProcessingRef.current = true;
        lastProcessedSearchRef.current = normalizedSearch;

        switchBusiness(
          found.location,
          found.brand,
          'businessUuid',
          businessUuid
        );
        return; // Exit early after switching
      }
    }

    // Mark current search as processed if no switching occurred
    lastProcessedSearchRef.current = normalizedSearch;
  }, [
    currentBusiness,
    brandList,
    dispatch,
    history,
    restaurantMailbox,
    businessUuid,
  ]);
};

export default useUpdateBusiness;
